package com.example.mystybag.appCore.logic.pages

import com.example.mystybag.appCore.model.pages.MerchantProfileDetailsUIState
import com.example.mystybag.common.resources.SR
import com.example.mystybag.communication.Communication
import com.example.mystybag.communication.defines.FileUploadTarget
import com.example.mystybag.communication.ext.CommandResult
import com.example.mystybag.communication.common.CommonCommands
import com.example.mystybag.communication.utils.Response
import com.example.mystybag.communication.utils.onResponse
import com.example.mystybag.core.expected.LogHelper
import com.example.mystybag.core.expected.PlatformHelperWrapper.openGallery
import com.example.mystybag.core.expected.PlatformHelperWrapper.takePhoto
import com.example.mystybag.core.expected.byteArrayFromFile
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.BottomSheetChild
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.core.logic.base.UIBottomSheetConfig
import com.example.mystybag.core.resources.SRC
import androidx.compose.ui.text.input.KeyboardType
import com.example.mystybag.appCore.logic.PhoneSelectionBottomSheetChild
import com.example.mystybag.appCore.logic.PhoneSelectionBottomSheetViewModel
import com.example.mystybag.common.logic.BSNavigatorChooseTakePhotoOrPickFile
import com.example.mystybag.common.logic.BSNavigatorPhoneInput
import com.example.mystybag.common.logic.BSNavigatorTextInput
import com.example.mystybag.common.logic.ChooseTakePhotoOrPickFileBottomSheetChild
import com.example.mystybag.common.logic.TextInputBottomSheetChild
import com.example.mystybag.common.logic.TextInputBottomSheetViewModel
import com.example.mystybag.common.model.ChooseTakePhotoOrPickFileVisualModel
import com.example.mystybag.common.model.TextInputBSVisualModel
import com.example.mystybag.core.defines.TextFieldType
import com.example.mystybag.cropImage.expected.getTempImageBytes
import com.example.mystybag.cropImage.logic.NavigatorProfileCropImage
import com.example.mystybag.cropImage.logic.ProfileImageCropperCallbacks
import com.example.mystybag.permissionsUtils.expected.Permission
import com.example.mystybag.permissionsUtils.expected.PermissionHelper.requestPermission
import com.example.mystybag.permissionsUtils.logic.DialogNavigatorPermissionSettings
import com.example.mystybag.sharedmodels.SCImage
import com.example.mystybag.sharedmodels.project.requestModels.merchant.GetMerchantProfileRequestModel
import com.example.mystybag.sharedmodels.project.requestModels.merchant.UpdateMerchantProfileRequestModel
import com.example.mystybag.core.extensions.safeCast
import kotlinx.coroutines.launch

class MerchantProfileDetailsViewModel(
    context: AppContext
) : ChildViewModel<MerchantProfileDetailsUIState>(context, MerchantProfileDetailsUIState()) {

    override fun onCreate() {
        navControls.title = sharedString(SR.strings.merchant_profile)
        navControls.backButtonVisible = true
        loadProfileDetails()
    }

    override fun configureBottomSheets(config: UIBottomSheetConfig, context: AppContext): BottomSheetChild = when (config) {
        is BSNavigatorChooseTakePhotoOrPickFile -> ChooseTakePhotoOrPickFileBottomSheetChild(
            title               = sharedString(SR.strings.company_logo),
            infoText            = listOf(),
            onInfoButtonClicked = { },
            additionalRows = arrayListOf(
                ChooseTakePhotoOrPickFileVisualModel(
                    text    = sharedString(SR.strings.take_photo),
                    image   = SRC.images.camera_fill_icon,
                    onClick = {
                        closeBottomSheet {
                            launchOnMain {
                                onCameraPermissionResponse(requestPermission(Permission.CaptureImage))
                            }
                        }
                    }
                ),
                ChooseTakePhotoOrPickFileVisualModel(
                    text    = sharedString(SR.strings.open_gallery),
                    image   = SRC.images.gallery_fill_icon,
                    onClick = {
                        closeBottomSheet {
                            launchOnMain { onGalleryPermissionResponse(requestPermission(Permission.OpenGallery)) }
                        }
                    }
                )
            ).also {
                if (uiState.value.logoImageFileName != null || uiState.value.logoImageUrl != null)
                    it.add(
                        ChooseTakePhotoOrPickFileVisualModel(
                            text        = sharedString(SR.strings.remove_photo),
                            image       = SRC.images.bin_fill_icon,
                            onClick     = { 
                                closeBottomSheet()
                                updateState { 
                                    it.copy(
                                        logoImageFileName = null,
                                        logoImageUrl = null
                                    ) 
                                }
                                saveProfileToServer()
                            }
                        )
                    )
            }
        )
        is BSNavigatorTextInput -> TextInputBottomSheetChild(viewModel = TextInputBottomSheetViewModel(context, config.visualModel))
        is BSNavigatorPhoneInput -> PhoneSelectionBottomSheetChild(viewModel = PhoneSelectionBottomSheetViewModel(context, config))
        else -> super.configureBottomSheets(config, context)
    }

    override fun parentCloseBottomSheet() {
        LogHelper.logEvent("[WebDialogCloseButton] MerchantProfileDetailsViewModel closeBottomSheet requested")
        closeBottomSheet()
    }

    fun onLogoButtonClicked() {
        openBottomSheet(BSNavigatorChooseTakePhotoOrPickFile)
    }

    fun onCompanyNameRowClick() {
        openBottomSheet(
            BSNavigatorTextInput(
                TextInputBSVisualModel(
                    initialText = uiState.value.companyName,
                    title = sharedString(SR.strings.company_name),
                    hint = sharedString(SR.strings.please_enter_company_name),
                    buttonText = sharedString(SR.strings.save),
                    keyboardType = KeyboardType.Text,
                    validate = { text ->
                        if (text.isBlank()) Pair(false, sharedString(SR.strings.please_enter_company_name))
                        else Pair(true, null)
                    },
                    onTextChanged = { newValue ->
                        onCompanyNameChange(newValue)
                        closeBottomSheet()
                        saveProfileToServer()
                    }
                )
            )
        )
    }

    fun onPhoneRowClick() {
        openBottomSheet(
            BSNavigatorPhoneInput(
                initialDialCode = uiState.value.dialCode,
                initialPhoneNumber = uiState.value.phoneNumber,
                onPhoneSelected = { dialCode, phoneNumber ->
                    onDialCodeChange(dialCode)
                    onPhoneNumberChange(phoneNumber)
                    closeBottomSheet()
                    saveProfileToServer()
                }
            )
        )
    }

    fun onBulstatRowClick() {
        openBottomSheet(
            BSNavigatorTextInput(
                TextInputBSVisualModel(
                    initialText = uiState.value.bulstat,
                    title = sharedString(SR.strings.bulstat),
                    hint = sharedString(SR.strings.bulstat),
                    buttonText = sharedString(SR.strings.save),
                    keyboardType = KeyboardType.Number,
                    validate = { text ->
                        if (text.isBlank()) Pair(false, sharedString(SR.strings.bulstat_required))
                        else Pair(true, null)
                    },
                    onTextChanged = { newValue ->
                        onBulstatChange(newValue)
                        closeBottomSheet()
                        saveProfileToServer()
                    }
                )
            )
        )
    }

    fun onIbanRowClick() {
        openBottomSheet(
            BSNavigatorTextInput(
                TextInputBSVisualModel(
                    initialText = uiState.value.iban,
                    title = sharedString(SR.strings.iban),
                    hint = sharedString(SR.strings.iban),
                    buttonText = sharedString(SR.strings.save),
                    keyboardType = KeyboardType.Text,
                    onTextChanged = { newValue ->
                        onIbanChange(newValue)
                        closeBottomSheet()
                        saveProfileToServer()
                    }
                )
            )
        )
    }

    fun onCompanyStreetRowClick() {
        openBottomSheet(
            BSNavigatorTextInput(
                TextInputBSVisualModel(
                    initialText = uiState.value.companyStreet,
                    title = sharedString(SR.strings.company_street),
                    hint = sharedString(SR.strings.company_street),
                    buttonText = sharedString(SR.strings.save),
                    keyboardType = KeyboardType.Text,
                    onTextChanged = { newValue ->
                        onCompanyStreetChange(newValue)
                        closeBottomSheet()
                        saveProfileToServer()
                    }
                )
            )
        )
    }

    private fun onGalleryPermissionResponse(isGranted: Boolean) {
        if (isGranted)
            launchOnMain { openGallery()?.image?.let { image -> cropImage(image) } }
        else
            showPermissionDialog(false)
    }

    private fun onCameraPermissionResponse(isGranted: Boolean) {
        if (isGranted)
            launchOnMain { takePhoto()?.image?.let { cropImage(it) } }
        else
            showPermissionDialog(true)
    }

    private fun cropImage(image: SCImage) {
        navigate(NavigatorProfileCropImage(
            selectedImage = image,
            callbacks = object: ProfileImageCropperCallbacks {
                override fun onImageCropped(imageFileName: String, originalImgFileName: String) {
                    goBack()
                    updateState {
                        it.copy(
                            logoImageFileName = imageFileName
                        )
                    }
                    saveProfileToServer()
                }
            },
            aspectRatio = 1f // Square aspect ratio for logo
        ))
    }

    private fun showPermissionDialog(isForCamera: Boolean) {
        uiThread {
            showDialog(
                DialogNavigatorPermissionSettings(
                    sharedString(
                        if (isForCamera)
                            SR.strings.camera_permission
                        else
                            SR.strings.gallery_permission
                    )
                )
            )
            .onPositiveClick {
                com.example.mystybag.core.expected.openSettings()
            }
        }
    }

    private fun loadProfileDetails() {
        scope.launch {
            updateState { it.copy(isLoading = true) }

            commandBlock(showProgress = false, hideProgress = false) {
                val merchantId = com.example.mystybag.core.expected.Preferences.getLong(
                    key = com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_ID,
                    0L
                )
                
                if (merchantId == 0L) {
                    launchOnMain {
                        updateState { it.copy(isLoading = false) }
                    }
                    return@commandBlock
                }

                CommonCommands.getMerchantProfile(
                    GetMerchantProfileRequestModel(merchantId = merchantId)
                )
                    .onResponse(
                        success = { response ->
                            launchOnMain {
                                val responseModel = response.safeCast<com.example.mystybag.sharedmodels.project.responseModels.merchant.GetMerchantProfileResponseModel>()
                                
                                if (responseModel != null) {
                                    val logoImageUrl = responseModel.logoImageUrl?.trim()
                                    updateState {
                                        it.copy(
                                            isLoading = false,
                                            email = responseModel.email ?: "",
                                            companyName = responseModel.companyName ?: "",
                                            dialCode = responseModel.dialCode ?: "",
                                            phoneNumber = responseModel.phoneNumber ?: "",
                                            bulstat = responseModel.bulstat ?: "",
                                            iban = responseModel.iban ?: "",
                                            logoImageUrl = if (!logoImageUrl.isNullOrBlank()) logoImageUrl else null,
                                            countryId = responseModel.countryId,
                                            cityId = responseModel.cityId,
                                            companyStreet = responseModel.companyStreet ?: ""
                                        )
                                    }
                                } else {
                                    updateState { it.copy(isLoading = false) }
                                }
                            }
                        },
                        baseError = { response ->
                            launchOnMain {
                                updateState { it.copy(isLoading = false) }
                            }
                        }
                    )
            }
        }
    }

    fun onCompanyNameChange(companyName: String) {
        updateState {
            it.copy(
                companyName = companyName,
                companyNameError = if (companyName.isEmpty()) sharedString(SR.strings.please_enter_company_name) else null
            )
        }
    }

    fun onDialCodeChange(dialCode: String) {
        updateState { it.copy(dialCode = dialCode) }
    }

    fun onPhoneNumberChange(phoneNumber: String) {
        updateState {
            it.copy(
                phoneNumber = phoneNumber,
                phoneNumberError = if (phoneNumber.isEmpty()) sharedString(SR.strings.please_enter_phone_number) else null
            )
        }
    }

    fun onBulstatChange(bulstat: String) {
        updateState {
            it.copy(
                bulstat = bulstat,
                bulstatError = if (bulstat.isEmpty()) sharedString(SR.strings.bulstat_required) else null
            )
        }
    }

    fun onIbanChange(iban: String) {
        val filteredIban = iban.filter { it.isLetterOrDigit() }.uppercase()

        updateState {
            it.copy(
                iban = filteredIban,
                ibanError = null
            )
        }
    }

    fun onCompanyStreetChange(companyStreet: String) {
        updateState {
            it.copy(
                companyStreet = companyStreet,
                companyStreetError = null
            )
        }
    }

    fun onSaveClick() {
        val state = uiState.value
        if (state.companyName.isEmpty()) {
            updateState { it.copy(companyNameError = sharedString(SR.strings.please_enter_company_name)) }
            return
        }
        if (state.phoneNumber.isEmpty()) {
            updateState { it.copy(phoneNumberError = sharedString(SR.strings.please_enter_phone_number)) }
            return
        }
        if (state.bulstat.isEmpty()) {
            updateState { it.copy(bulstatError = sharedString(SR.strings.bulstat_required)) }
            return
        }
        saveProfileToServer()
    }

    private fun saveProfileToServer() {
        val state = uiState.value
        if (state.companyName.isEmpty() || state.phoneNumber.isEmpty() || state.bulstat.isEmpty()) return

        scope.launch {
            updateState { it.copy(isSaving = true) }

            commandBlock(showProgress = true) {
                val merchantId = com.example.mystybag.core.expected.Preferences.getLong(
                    key = com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_ID,
                    0L
                )

                if (merchantId == 0L) {
                    launchOnMain { updateState { it.copy(isSaving = false) } }
                    return@commandBlock
                }

                var logoImageUrl: String? = null
                state.logoImageFileName?.let { fileName ->
                    val bytes = byteArrayFromFile(fileName) ?: getTempImageBytes(fileName)
                    if (bytes != null) {
                        when (val res = Communication.uploadFile(FileUploadTarget.MERCHANTS, bytes, "logo.jpg")) {
                            is CommandResult.Success -> logoImageUrl = res.successModel.url
                            else -> {
                                val err = (res as? CommandResult.BaseError)?.baseErrorModel
                                launchOnMain {
                                    updateState { it.copy(isSaving = false) }
                                    showSnackbar(Response.getErrorMessage(err?.status ?: -1, err?.apiEndPoint ?: "", err?.statusMessage))
                                }
                                return@commandBlock
                            }
                        }
                    }
                }

                CommonCommands.updateMerchantProfile(
                    UpdateMerchantProfileRequestModel(
                        merchantId = merchantId,
                        companyName = state.companyName,
                        dialCode = state.dialCode,
                        phoneNumber = state.phoneNumber,
                        bulstat = state.bulstat,
                        iban = state.iban.takeIf { it.isNotEmpty() },
                        logoImageUrl = logoImageUrl,
                        countryId = state.countryId,
                        cityId = state.cityId,
                        companyStreet = state.companyStreet.takeIf { it.isNotEmpty() }
                    )
                )
                    .onResponse(
                        success = {
                            launchOnMain {
                                updateState { it.copy(isSaving = false) }
                            }
                        },
                        baseError = { response ->
                            launchOnMain {
                                updateState { it.copy(isSaving = false) }
                                showSnackbar(com.example.mystybag.communication.utils.Response.getErrorMessage(response.status, response.apiEndPoint, response.statusMessage))
                            }
                        }
                    )
            }
        }
    }
}
